// PNG reader for the 16-bit path (§6.1 decode step 4: 16-bit sources bypass the canvas).
// Supports colour types 0, 2, 4, 6 at 8 or 16 bit, non-interlaced. Anything else → `null` and the
// caller falls back to the browser (8-bit, colour-managed) path.
import { unzlibSync } from 'fflate';

export type PngHeader = {
  width: number;
  height: number;
  bitDepth: number;
  colorType: number;
  interlace: number;
};
export type PngColor = { iccName?: string; icc?: Uint8Array; srgbChunk: boolean };

function readChunks(b: Uint8Array) {
  if (!(b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47)) throw new Error('not a PNG');
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  const chunks: { type: string; data: Uint8Array }[] = [];
  let o = 8;
  while (o + 8 <= b.length) {
    const len = dv.getUint32(o);
    const type = String.fromCharCode(b[o + 4]!, b[o + 5]!, b[o + 6]!, b[o + 7]!);
    chunks.push({ type, data: b.subarray(o + 8, o + 8 + len) });
    o += 12 + len;
    if (type === 'IEND') break;
  }
  return chunks;
}

export function readPngHeader(b: Uint8Array): { header: PngHeader; color: PngColor } {
  const chunks = readChunks(b);
  const ihdr = chunks.find((c) => c.type === 'IHDR');
  if (!ihdr) throw new Error('PNG without IHDR');
  const dv = new DataView(ihdr.data.buffer, ihdr.data.byteOffset, ihdr.data.byteLength);
  const header = {
    width: dv.getUint32(0),
    height: dv.getUint32(4),
    bitDepth: ihdr.data[8]!,
    colorType: ihdr.data[9]!,
    interlace: ihdr.data[12]!,
  };
  const color: PngColor = { srgbChunk: chunks.some((c) => c.type === 'sRGB') };
  const iccp = chunks.find((c) => c.type === 'iCCP');
  if (iccp) {
    const nul = iccp.data.indexOf(0);
    color.iccName = String.fromCharCode(...iccp.data.subarray(0, nul));
    try {
      color.icc = unzlibSync(iccp.data.subarray(nul + 2));
    } catch {
      /* unreadable profile: treat as untagged */
    }
  }
  return { header, color };
}

const CHANNELS: Record<number, number> = { 0: 1, 2: 3, 4: 2, 6: 4 };

/** Decodes to interleaved RGBA Uint16 (8-bit sources are widened ×257). Returns null if unsupported. */
export function decodePngToRgba16(b: Uint8Array): {
  width: number;
  height: number;
  data: Uint16Array;
  hasAlpha: boolean;
  header: PngHeader;
  color: PngColor;
} | null {
  const { header, color } = readPngHeader(b);
  const ch = CHANNELS[header.colorType];
  if (!ch || header.interlace !== 0 || (header.bitDepth !== 8 && header.bitDepth !== 16)) return null;
  const chunks = readChunks(b).filter((c) => c.type === 'IDAT');
  let total = 0;
  for (const c of chunks) total += c.data.length;
  const z = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    z.set(c.data, o);
    o += c.data.length;
  }
  const raw = unzlibSync(z);
  const { width, height } = header;
  const bps = header.bitDepth / 8;
  const bpp = ch * bps;
  const stride = width * bpp;
  if (raw.length < (stride + 1) * height) throw new Error('PNG data truncated');
  const cur = new Uint8Array(stride);
  const prev = new Uint8Array(stride);
  const out = new Uint16Array(width * height * 4);
  let hasAlpha = false;
  for (let y = 0; y < height; y++) {
    const base = y * (stride + 1);
    const ft = raw[base]!;
    for (let i = 0; i < stride; i++) {
      const x = raw[base + 1 + i]!;
      const a = i >= bpp ? cur[i - bpp]! : 0;
      const up = prev[i]!;
      const c = i >= bpp ? prev[i - bpp]! : 0;
      let v: number;
      switch (ft) {
        case 0:
          v = x;
          break;
        case 1:
          v = x + a;
          break;
        case 2:
          v = x + up;
          break;
        case 3:
          v = x + ((a + up) >>> 1);
          break;
        case 4: {
          const p = a + up - c;
          const pa = Math.abs(p - a),
            pb = Math.abs(p - up),
            pc = Math.abs(p - c);
          v = x + (pa <= pb && pa <= pc ? a : pb <= pc ? up : c);
          break;
        }
        default:
          throw new Error(`bad PNG filter ${ft}`);
      }
      cur[i] = v & 255;
    }
    for (let xPix = 0; xPix < width; xPix++) {
      const s = xPix * bpp;
      const sample = (k: number) =>
        bps === 2 ? (cur[s + k * 2]! << 8) | cur[s + k * 2 + 1]! : cur[s + k]! * 257;
      const d = (y * width + xPix) * 4;
      if (ch === 1 || ch === 2) {
        const g = sample(0);
        out[d] = out[d + 1] = out[d + 2] = g;
        out[d + 3] = ch === 2 ? sample(1) : 65535;
      } else {
        out[d] = sample(0);
        out[d + 1] = sample(1);
        out[d + 2] = sample(2);
        out[d + 3] = ch === 4 ? sample(3) : 65535;
      }
      if (out[d + 3] !== 65535) hasAlpha = true;
    }
    prev.set(cur);
  }
  return { width, height, data: out, hasAlpha, header, color };
}
